import * as yup from "yup";
import React, { useEffect, useState } from "react";
import JumboDemoCard from "@jumbo/components/JumboDemoCard/JumboDemoCard";
import Div from "@jumbo/shared/Div";
import useSwalWrapper from "@jumbo/vendors/sweetalert2/hooks";
import LoadingButton from "@mui/lab/LoadingButton";
import {
    FormControl,
    TextField,
    Typography,
    InputLabel,
    MenuItem,
    Select,
} from "@mui/material";
import { useFormik } from "formik";
import DndWrapper from "./DndWrapper";
import { useDropzone } from "react-dropzone";
import subTaskService from "app/services/subtask_service";
import { docBaseUrl, hraxios } from "app/services/config";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import moment from "moment";
import { useQuery } from "react-query";

const thumbsContainer = {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 16,
};

const thumb = {
    display: "inline-flex",
    borderRadius: 2,
    border: "1px solid #eaeaea",
    marginBottom: 8,
    marginRight: 8,
    width: 100,
    height: 100,
    padding: 4,
    boxSizing: "border-box",
};

const thumbInner = {
    display: "flex",
    minWidth: 0,
    overflow: "hidden",
};

const img = {
    display: "block",
    width: "auto",
    height: "100%",
};

const validationSchema = yup.object().shape({
    expenseAmount: yup
        .number()
        .typeError("Expense amount must be a number")
        .required("Expense amount is required")
        .test(
            "is-decimal",
            "Invalid format. Please enter a valid number ",
            (value) =>
                value === undefined || value === null
                    ? true
                    : /^(\d+)?(\.\d{1,2})?$/.test(value.toString())
        ),
    expenseDate: yup.string().required("Expense Date is required"),
    expenseDescription: yup.string().required("Description is required"),
    category: yup
        .array()
        .min(1, "Please select at least one category")
        .of(yup.string())
        .required("Category is required"),
    company: yup
        .array()
        .min(1, "Please select at least one company")
        .of(yup.string())
        .required("Company is required"),
    projects: yup
        .array()
        .min(1, "Please select at least one project")
        .of(yup.string())
        .required("Project is required"),
});

const OfficeExpenseForm = ({ expanseId }) => {
    const Swal = useSwalWrapper();
    const [files, setFiles] = useState([]);
    const [expenseDoc, setExpenseDoc] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [expenseData, setExpenseData] = React.useState({});
    const { hideDialog } = useJumboDialog();
    const { setAuthToken } = useJumboAuth();

    const { getRootProps, getInputProps } = useDropzone({
        accept: "image/*,application/pdf",
        onDrop: (acceptedFiles) => {
            const newFiles = acceptedFiles.map((file) =>
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                })
            );
            setFiles((prevFiles) => [...prevFiles, ...newFiles]);
            onFileUploadInput(acceptedFiles);
        },
    });

    useEffect(() => {
        if (expanseId !== undefined) {
            subTaskService
                .getHrExpenseDataById(expanseId)
                .then((data) => {
                    setExpenseData(data?.data?.data);
                    setExpenseDoc(data?.data?.data?.bill_reciept_documents);
                })
                .catch((err) => {
                    if (err?.response?.status === 401) {
                        setAuthToken(null);
                    }
                });
        }
    }, [expanseId, setAuthToken]);

    const thumbs = files.map((file) => (
        <div style={thumb} key={file.name}>
            <div style={thumbInner}>
                <img src={file.preview} style={img} alt="" />
            </div>
        </div>
    ));

    const onFileUploadInput = async (file) => {
        const fileLength = file.length;
        let allDoc = [];
        setIsLoading(true);
        if (fileLength > 0) {
            for (let i = 0; i < fileLength; i++) {
                let form_Data = new FormData();
                form_Data.append("attachment", file[i]);
                const response = await hraxios.post(
                    `hr_office_expenses/expenses-upload`,
                    form_Data
                );
                if (response.status === 200) {
                    let newDoc = {
                        key: response?.data?.data?.id?.key,
                        base_url: response?.data?.data?.id?.base_url,
                    };
                    allDoc.push(newDoc);
                } else {
                    sweetAlerts("error");
                }
            }
            setExpenseDoc((prevFiles) => [...prevFiles, ...allDoc]);
            setIsLoading(false);
        } else {
            setIsLoading(false);
            sweetAlerts("error");
        }
    };

    const sweetAlerts = (variant) => {
        Swal.fire({
            icon: variant,
            title: variant,
            text:
                variant === "success"
                    ? "Expense Entered Successfully!"
                    : "Something went wrong",
        }).then(() => {
            window.location.reload();
        });
    };

    const { data: projectsList } = useQuery({
        queryKey: ["products-list"],
        queryFn: async () => {
            const response = await hraxios.get(
                "custom_master_data/by_name?name=Projects"
            );
            return response.data.data.data?.custom_list || [];
        },
    });

    const { data: companyList } = useQuery({
        queryKey: ["company-list"],
        queryFn: async () => {
            const response = await hraxios.get("company");
            const companyData = response.data.data.data[0] || [];
            return companyData?.data?.map((item) => item.code) || [];
        },
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            expenseAmount: expenseData?.expense_amount || null,
            expenseDate: expenseData?.expense_date
                ? moment(expenseData?.expense_date).format("YYYY-MM-DD")
                : "",
            expenseDescription: expenseData?.expense_description || "",
            expenseNote: expenseData?.expense_note || "",
            category: expenseData?.category || "",
            company: expenseData?.company || "",
            projects: expenseData?.projects || "",
            bill_reciept_documents: expenseData?.bill_reciept_documents || [],
        },
        validationSchema,
        onSubmit: (values) => {
            if (expenseDoc?.length === 0) {
                return false;
            }
            let payload = {
                expense_description: values?.expenseDescription,
                expense_amount: values?.expenseAmount,
                expense_date: values?.expenseDate,
                bill_reciept_documents: expenseDoc,
                status: "Requested",
                expense_note: values?.expenseNote,
                category: values?.category,
                company: values?.company,
                projects: values?.projects,
            };
            if (expanseId !== undefined) {
                subTaskService
                    .updateExpenseStatus(payload, expanseId)
                    .then(() => {
                        hideDialog();
                        sweetAlerts("success");
                    })
                    .catch((err) => {
                        hideDialog();
                        if (err?.response?.status === 401) {
                            sweetAlerts("error");
                        }
                    });
            } else {
                subTaskService
                    .addExpensePostApi(payload)
                    .then(() => {
                        hideDialog();
                        sweetAlerts("success");
                    })
                    .catch((err) => {
                        hideDialog();
                        if (err?.response?.status === 401) {
                            sweetAlerts("error");
                        }
                    });
                sweetAlerts("success");
            }
        },
    });

    const {
        errors,
        handleChange,
        values,
        setFieldValue,
        dirty,
        handleSubmit,
        touched,
    } = formik;

    return (
        <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Div
                sx={{
                    "& .MuiTextField-root": {
                        mb: 3,
                    },
                }}
            >
                <FormControl sx={{ my: 2, width: "-webkit-fill-available" }}>
                    <TextField
                        fullWidth
                        type="number"
                        value={values?.expenseAmount}
                        label="Expense Amount"
                        name="expenseAmount"
                        onChange={handleChange}
                        style={{ marginBottom: "2px" }}
                        error={touched.expenseAmount && Boolean(errors.expenseAmount)}
                        helperText={touched.expenseAmount && errors.expenseAmount}
                    />
                </FormControl>

                <FormControl sx={{ width: "-webkit-fill-available" }}>
                    <TextField
                        id="expenseDate"
                        label="Expense Date"
                        type="date"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        onChange={handleChange}
                        value={values?.expenseDate}
                        style={{ marginBottom: "2px" }}
                        error={Boolean(errors.expenseDate && touched.expenseDate)}
                        helperText={touched.expenseDate && errors.expenseDate}
                    />
                </FormControl>

                <FormControl sx={{ my: 2, width: "-webkit-fill-available" }}>
                    <TextField
                        fullWidth
                        type="text"
                        value={values?.expenseDescription}
                        placeholder="Description.."
                        label="Description"
                        variant="outlined"
                        name="expenseDescription"
                        multiline
                        onChange={handleChange}
                        style={{ marginBottom: "2px" }}
                        error={Boolean(errors.expenseDescription && touched.expenseDescription)}
                        helperText={touched.expenseDescription && errors.expenseDescription}
                    />
                </FormControl>

                <FormControl sx={{ mt: 1, mb: 3 }} fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                        name="category"
                        multiple
                        value={values?.category || []}
                        renderValue={(selected) => selected.join(", ")}
                        onChange={(e) => {
                            const selectedValues = Array.isArray(e.target.value)
                                ? e.target.value
                                : [e.target.value];
                            setFieldValue("category", selectedValues);
                        }}
                    >
                        <MenuItem value={"travel"}>Travel</MenuItem>
                        <MenuItem value={"food"}>Food</MenuItem>
                        <MenuItem value={"accomodation"}>Accomodation</MenuItem>
                        <MenuItem value={"rentals"}>Rentals</MenuItem>
                        <MenuItem value={"internet"}>Internet</MenuItem>
                        <MenuItem value={"subscriptions"}>Subscriptions</MenuItem>
                        <MenuItem value={"others"}>Others</MenuItem>
                    </Select>
                    {formik.touched.category && formik.errors.category &&
                        <Typography color="error">{formik.errors.category}</Typography>}
                </FormControl>

                <FormControl sx={{ mt: 1, mb: 3 }} fullWidth>
                    <InputLabel>Company</InputLabel>
                    <Select
                        name="company"
                        multiple={false}
                        value={values?.company || []}
                        renderValue={(selected) => selected.join(", ")}
                        onChange={(e) => {
                            const selectedValues = Array.isArray(e.target.value)
                                ? e.target.value
                                : [e.target.value];
                            setFieldValue("company", selectedValues);
                        }}
                    >
                        {companyList?.map((company) => (
                            <MenuItem key={company} value={company}>
                                {company}
                            </MenuItem>
                        ))}
                    </Select>
                    {formik.touched.company && formik.errors.company &&
                        <Typography color="error">{formik.errors.company}</Typography>}
                </FormControl>

                <FormControl sx={{ mt: 1, mb: 3 }} fullWidth>
                    <InputLabel>Projects</InputLabel>
                    <Select
                        name="projects"
                        multiple={false}
                        value={values?.projects || []}
                        renderValue={(selected) => selected.join(", ")}
                        onChange={(e) => {
                            setFieldValue("projects", [].concat(e.target.value));
                        }}
                    >
                        {projectsList?.map((project) => (
                            <MenuItem key={project} value={project}>
                                {project}
                            </MenuItem>
                        ))}
                    </Select>
                    {formik.touched.projects && formik.errors.projects &&
                        <Typography color="error">{formik.errors.projects}</Typography>}
                </FormControl>

                <FormControl sx={{ mt: 1, width: "-webkit-fill-available" }}>
                    <TextField
                        fullWidth
                        type="text"
                        value={values?.expenseNote}
                        placeholder="Note.."
                        label="Note"
                        variant="outlined"
                        name="expenseNote"
                        multiline
                        onChange={handleChange}
                    />
                </FormControl>

                <JumboDemoCard title={"Dropzone with preview"}>
                    <Div>
                        <div {...getRootProps({ className: "dropzone" })}>
                            <DndWrapper>
                                <input {...getInputProps()} name="expenseDoc" />
                                <Typography variant="body1">
                                    Drag 'n' drop some files here, or click to select files
                                </Typography>
                            </DndWrapper>
                        </div>

                        <aside style={thumbsContainer}>{thumbs}</aside>

                        <Div
                            sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}
                        >
                            {formik.values.bill_reciept_documents.map((doc, index) => {
                                const fileUrl = `${docBaseUrl}${doc?.base_url}/${doc?.key}`;
                                const isImage = /\.(jpg|jpeg|png|gif)$/i.test(doc?.key);
                                const isPdf = /\.pdf$/i.test(doc?.key);

                                return (
                                    <Div
                                        key={index}
                                        sx={{
                                            border: "1px solid #ccc",
                                            borderRadius: "6px",
                                            p: 1,
                                            textAlign: "center",
                                            width: 120,
                                        }}
                                    >
                                        {isImage && (
                                            <img
                                                src={fileUrl}
                                                alt={doc?.key}
                                                style={{
                                                    width: "100%",
                                                    height: "100px",
                                                    objectFit: "cover",
                                                }}
                                            />
                                        )}
                                        {isPdf && (
                                            <embed
                                                src={fileUrl}
                                                type="application/pdf"
                                                width="100%"
                                                height="100px"
                                            />
                                        )}
                                        <Typography
                                            variant="body2"
                                            noWrap
                                            sx={{ mt: 1 }}
                                            title={doc?.key}
                                        >
                                            {index + 1}. {doc?.key}
                                        </Typography>
                                    </Div>
                                );
                            })}
                        </Div>
                    </Div>
                </JumboDemoCard>

                {dirty && expenseDoc?.length === 0 && (
                    <div style={{ color: "red", marginTop: "2px" }}>
                        Attachment is required
                    </div>
                )}

                <LoadingButton
                    fullWidth
                    type="submit"
                    variant="contained"
                    size="large"
                    sx={{ mb: 3 }}
                    disabled={isLoading}
                    loading={formik.isSubmitting}
                >
                    {formik.isSubmitting ? "Uploading..." : "Save"}
                </LoadingButton>
            </Div>
        </form>
    );
};

OfficeExpenseForm.defaultProps = {
    onSave: () => { },
};

export default OfficeExpenseForm;